/**
 * Sincronizzazione dell'auth fra le tab dello stesso browser.
 *
 * `authStore` è l'unico store Akita persistito su localStorage anziché su IndexedDB (vedi
 * `localforage.settings.ts`): è per questo che la sincronizzazione può appoggiarsi all'evento
 * `storage`, che IndexedDB non emette.
 */

import { AUTH_SNAPSHOT_STORAGE_KEY, LEGACY_CACHE_EPOCH } from '@persistence/session-cache';

export { AUTH_SNAPSHOT_STORAGE_KEY };

export type AuthTabSyncOutcome =
  /** La sessione continua e resta la stessa (tipicamente un rinnovo del token): allinea gli store. */
  | { action: 'apply'; stores: Record<string, any> }
  /** Un'altra tab ha chiuso la sessione: anche questa deve uscire. */
  | { action: 'end-session' }
  /** Un'altra tab ha fatto login mentre questa era fuori: anche questa deve entrare. */
  | { action: 'session-started' }
  /** La stessa tab/browser e' passata a un'altra identita': serve un runtime nuovo. */
  | { action: 'session-changed' }
  /** Niente da fare: valore inutilizzabile, o cambiamento che questa tab ha già assorbito. */
  | { action: 'ignore' };

/**
 * Decide cosa fare del valore arrivato con l'evento `storage` per la chiave dell'auth.
 *
 * Lo stesso evento copre tre fatti diversi, e in origine ne veniva gestito uno solo (l'applicazione
 * dello snapshot). Distinguerli è ciò che tiene allineate le tab dello stesso browser in entrambe le
 * direzioni: il logout di una tab porta fuori le altre, e il login di una tab fa entrare le altre.
 * Token presente/assente distingue login e logout; cacheEpoch distingue un refresh token ordinario
 * da un vero cambio di identita', che deve ricreare l'intero runtime.
 *
 * Il parse avviene qui e non dentro `snapshotManager.setStoresSnapshot` per un motivo preciso: quella
 * funzione alza il flag globale `skipStorageUpdate` PRIMA del proprio `JSON.parse` e lo riabbassa
 * solo alla fine. Se il parse (o il successivo `Object.keys`) lancia, il flag resta alzato per sempre
 * e ogni `persistState` della tab smette di scrivere, senza alcun segnale. Il flag non è esportato
 * pubblicamente da Akita, quindi non è ripristinabile dall'esterno: l'unica difesa è non farla
 * lanciare, cioè passarle un oggetto già validato.
 */
export function resolveAuthTabSync(
  raw: string | null,
  currentAccessToken: string | null,
  currentCacheEpoch: string = LEGACY_CACHE_EPOCH,
): AuthTabSyncOutcome {
  // Chiave rimossa o svuotata: è il logout, che ripulisce il localStorage. Non c'è nessuno snapshot
  // da applicare, il fatto è che la sessione è finita.
  if (!raw) {
    return endedOrIgnored(currentAccessToken);
  }

  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    // Dato corrotto: non dice nulla sulla sessione. Cambiare stato su un'ipotesi sarebbe peggio che
    // restare come si è, tanto il prossimo evento valido riallinea la tab.
    console.error('[AUTH-TAB-SYNC] snapshot ignorato: JSON non valido', error);
    return { action: 'ignore' };
  }

  if (!parsed || typeof parsed !== 'object') {
    console.error('[AUTH-TAB-SYNC] snapshot ignorato: forma inattesa', typeof parsed);
    return { action: 'ignore' };
  }

  // Senza la slice `auth` lo snapshot non dice nulla sulla sessione: anomalia, non cambio di stato.
  if (!parsed.auth || typeof parsed.auth !== 'object') {
    console.error('[AUTH-TAB-SYNC] snapshot ignorato: slice auth assente');
    return { action: 'ignore' };
  }

  if (!parsed.auth.accessToken) {
    return endedOrIgnored(currentAccessToken);
  }

  // Token dove questa tab non ne aveva: un'altra tab ha fatto login. Non basta allineare gli store,
  // altrimenti la tab resta ferma sulla pagina di login con una sessione valida in mano.
  if (!currentAccessToken) {
    return { action: 'session-started' };
  }

  const incomingCacheEpoch = parsed.auth.cacheEpoch || LEGACY_CACHE_EPOCH;
  if (incomingCacheEpoch !== (currentCacheEpoch || LEGACY_CACHE_EPOCH)) {
    return { action: 'session-changed' };
  }

  return { action: 'apply', stores: parsed };
}

/**
 * La fine di una sessione riguarda solo chi ne aveva una. Una tab già fuori (tipicamente ferma sul
 * login) non ha niente da chiudere, e trattarla come le altre significherebbe ricaricarla per nulla.
 */
function endedOrIgnored(currentAccessToken: string | null): AuthTabSyncOutcome {
  return currentAccessToken ? { action: 'end-session' } : { action: 'ignore' };
}
